import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PromptView } from '@agentic-os/contracts';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';
import { Button, ErrorNote, Mono, PageHeader } from '@/components/ui/primitives';
import { DomainStatus } from '@/components/ui/status-badge';
import { api, formatTime } from '@/lib/api';
import { useSession } from '@/lib/session';

/**
 * Prompt registry (07-PROMPT-ENGINE). Versions move DRAFT → TESTING → PUBLISHED → DEPRECATED;
 * published content is immutable. Publication is an explicit, audited act — workflows only use
 * PUBLISHED versions. Platform prompts can be published by platform administrators only.
 */
export function PromptsPage() {
  const { can } = useSession();
  const manage = can('prompts.manage');
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['prompts'],
    queryFn: () => api<{ items: PromptView[] }>('/api/v1/prompts'),
  });
  const change = useMutation({
    mutationFn: (a: { id: string; status: string }) =>
      api(`/api/v1/prompts/versions/${a.id}/status`, {
        method: 'POST',
        body: { status: a.status },
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['prompts'] }),
  });
  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <PageHeader
        title="Prompts"
        description="Versioned prompts. Only PUBLISHED versions are used by workflows; published content never changes."
      />
      {list.isError && <ErrorNote error={list.error} />}
      {change.isError && <ErrorNote error={change.error} />}
      {(list.data?.items ?? []).map((p) => (
        <Card key={p.id}>
          <CardHeader>
            <div className="flex flex-wrap items-center gap-2">
              <CardTitle>{p.name}</CardTitle>
              <Mono>{p.key}</Mono>
              <span className="text-xs text-muted">
                {p.scope === 'platform' ? 'Platform prompt' : 'Organization prompt'} · {p.domain}
              </span>
            </div>
            {p.description && <p className="mt-1 text-sm text-muted">{p.description}</p>}
          </CardHeader>
          <CardBody className="space-y-4">
            {p.versions.map((v) => (
              <details
                key={v.id}
                className="rounded-md border border-border"
                open={v.status === 'draft'}
              >
                <summary className="flex cursor-pointer flex-wrap items-center gap-2 px-3 py-2 text-sm">
                  <span className="font-medium">v{v.version}</span>
                  <DomainStatus value={v.status} />
                  <span className="text-xs text-muted">
                    profile <Mono>{v.modelProfile}</Mono> · output <Mono>{v.outputSchemaRef}</Mono>{' '}
                    · created {formatTime(v.createdAt)}
                    {v.publishedAt && <> · published {formatTime(v.publishedAt)}</>}
                  </span>
                </summary>
                <div className="space-y-3 border-t border-border px-3 py-3">
                  {v.changeNote && <p className="text-xs text-muted">{v.changeNote}</p>}
                  <div>
                    <p className="text-xs font-medium text-muted">System template</p>
                    <pre className="mt-1 whitespace-pre-wrap rounded bg-surface-muted p-2 font-mono text-xs">
                      {v.systemTemplate}
                    </pre>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted">User template</p>
                    <pre className="mt-1 whitespace-pre-wrap rounded bg-surface-muted p-2 font-mono text-xs">
                      {v.userTemplate}
                    </pre>
                  </div>
                  <p className="text-xs text-muted">
                    Variables:{' '}
                    {v.variables
                      .map((x) => `${x.name}${x.untrusted ? ' (untrusted data)' : ''}`)
                      .join(', ')}{' '}
                    · checksum <Mono>{v.checksum.slice(0, 16)}…</Mono>
                  </p>
                  {manage && (
                    <div className="flex gap-1.5">
                      {(v.status === 'draft' || v.status === 'testing') && (
                        <Button
                          variant="primary"
                          onClick={() =>
                            window.confirm(
                              `Publish ${p.key} v${v.version}? Published content can never change.`,
                            ) && change.mutate({ id: v.id, status: 'published' })
                          }
                        >
                          Publish
                        </Button>
                      )}
                      {v.status === 'draft' && (
                        <Button onClick={() => change.mutate({ id: v.id, status: 'testing' })}>
                          Mark as testing
                        </Button>
                      )}
                      {v.status === 'published' && (
                        <Button
                          variant="danger"
                          onClick={() => change.mutate({ id: v.id, status: 'deprecated' })}
                        >
                          Deprecate
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </details>
            ))}
          </CardBody>
        </Card>
      ))}
      {list.data?.items.length === 0 && <p className="text-sm text-muted">No prompts.</p>}
    </div>
  );
}
